'use client';

import { useMemo, useState } from 'react';
import { UserContext } from './contexts/UserContext';
import { ProductType } from '../utils/productDefinition';
import { Locale } from '../i18n-config';
import { ClientType, ConfiguratorOriginsType } from '../utils/constants';
import { isFalsy } from '../utils/utils';

export type UserContextProviderProps = {
  children: React.ReactNode;
  defaultAccountNr?: string;
  defaultCountryCode: string;
  defaultClient: ClientType;
  defaultLanguage: Locale;
  defaultProduct: ProductType | undefined;
  defaultOrigin: ConfiguratorOriginsType;
  defaultRtwProductCodes: string[];
  defaultStoreID?: string;
  defaultLocationCode?: string;
  defaultSizeId?: string;
  defaultChannelId?: string;
};

export const UserContextProvider = ({
  children,
  defaultAccountNr,
  defaultCountryCode,
  defaultClient,
  defaultLanguage,
  defaultProduct,
  defaultOrigin,
  defaultRtwProductCodes,
  defaultStoreID,
  defaultLocationCode,
  defaultSizeId,
  defaultChannelId,
}: UserContextProviderProps) => {
  const [accountNr, setAccountNr] = useState<string | undefined>(defaultAccountNr);
  const [countryCode, setCountryCode] = useState<string>(defaultCountryCode);
  const [client, setClient] = useState<ClientType>(defaultClient);
  const [language, setLanguage] = useState<Locale>(defaultLanguage);
  const [product, setProduct] = useState<ProductType | undefined>(defaultProduct);
  const [origin, setOrigin] = useState<ConfiguratorOriginsType>(defaultOrigin);
  const [rtwProductCodes, setRtwProductCodes] = useState<string[]>(defaultRtwProductCodes);
  const [storeID, setStoreID] = useState<string | undefined>(defaultStoreID);
  const [locationCode, setLocationCode] = useState<string | undefined>(defaultLocationCode);
  const [sizeId, setSizeId] = useState<string | undefined>(defaultSizeId);
  const [channelId, setChannelId] = useState<string | undefined>(defaultChannelId);
  const [isUserPreselected, setIsUserPreselected] = useState<boolean>(!isFalsy(defaultAccountNr));
  const contextValue = useMemo(() => {
    return {
      accountNr,
      setAccountNr,
      countryCode,
      setCountryCode,
      client,
      setClient,
      language,
      setLanguage,
      product,
      setProduct,
      origin,
      setOrigin,
      rtwProductCodes,
      setRtwProductCodes,
      storeID,
      setStoreID,
      locationCode,
      setLocationCode,
      sizeId,
      setSizeId,
      channelId,
      setChannelId,
      isUserPreselected,
      setIsUserPreselected,
    };
  }, [
    accountNr,
    countryCode,
    client,
    language,
    product,
    origin,
    rtwProductCodes,
    storeID,
    locationCode,
    sizeId,
    channelId,
    isUserPreselected,
  ]);

  return <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>;
};
